import { useState, useMemo, Fragment } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  formatPrice,
  parsePrice,
  type Channel,
  type ChannelBusinessPricingRule,
  type ChannelCapabilities,
  type CurrentUser,
  type Listing,
  type ListingStatus,
  type ProductFilter,
  type ProductRow,
  type QueueOutcome,
  type SiteCode,
  type SkuCopyResult,
} from '../lib/api';
import { businessPriceRuleLabel, deriveBusinessPrice, MIRROR_RETAIL, type BusinessPriceRule } from '@mfs/shared';
import { ConfirmWriteDialog } from './ConfirmWriteDialog';
import { ChannelDetail, tableColumnSpan } from './ChannelDetail';
import { PriceCell } from './PriceCell';
import { SkuCopyReport } from './SkuCopyReport';
import { ChannelTag, Change } from './ChangeBits';

/**
 * The main table: one row per SKU, one column per channel.
 *
 * A channel cell shows that channel's price, editable in place. A marketplace
 * cell is marked when its price is out of step with the *other* marketplaces —
 * that drift is the thing the whole tool exists to make visible. The website
 * prices independently, so it is left out of the comparison and never marked.
 */

interface Props {
  user: CurrentUser;
  /**
   * Which tab this instance is. 'inactive' shows products a channel is
   * withholding; a listing that goes live again simply stops matching and
   * reappears on the main tab after the next sync.
   */
  status?: ListingStatus;
}

interface PendingWrite {
  listing: Listing;
  field: 'price' | 'title' | 'sku';
  newValue: number | string;
  productTitle: string;
  /** Only ever set for price — title and sku never trigger the large-change guard. */
  percentChange: number;
}

/**
 * A field edit committed locally (Enter/blur) but not yet sent to the
 * server — the whole point of the "Queue Changes" step is to let several of
 * these pile up on one product before any of them reach the queue and pull
 * the row into the in-flight section. Keyed by `${listing.id}:${field}` —
 * see draftKey below — since a listing's fields stage independently.
 */
export interface DraftEdit {
  listing: Listing;
  field: 'price' | 'title' | 'sku';
  value: number | string;
  productTitle: string;
}

/** One draft edit still to submit, paired with the state-map key it lives under. */
interface QueuedDraft {
  key: string;
  entry: DraftEdit;
}

export function ProductTable({ user, status = 'active' }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  // The text box's own live value. Kept separate from `search` — which drives
  // the query — so typing doesn't refetch on every keystroke; only Enter commits.
  const [searchInput, setSearchInput] = useState('');
  const [filter, setFilter] = useState<ProductFilter>('all');
  const [site, setSite] = useState<'all' | SiteCode>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Which channel rows have their detail open, keyed `${productId}:${channelId}`.
  // Held here rather than inside ChannelDetail, which unmounts whenever its
  // product collapses — state living there would be dropped without warning.
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
  // Which of the two table sections (in-flight, all products) are collapsed.
  // Neither starts collapsed — the whole point of pinning in-flight products
  // is to be seen without an extra click.
  const [collapsedSections, setCollapsedSections] = useState<Set<'in-flight' | 'all'>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingWrite | null>(null);
  // Local, unqueued field edits — see DraftEdit. Nothing here has reached the
  // server; a row stays out of the in-flight section until "Queue Changes"
  // (or Ctrl+Enter) actually submits them.
  const [draftEdits, setDraftEdits] = useState<Map<string, DraftEdit>>(new Map());
  // Which products currently have a "Queue Changes" submission in flight —
  // disables that product's button/discard link for the duration, including
  // while paused on a needs_confirmation dialog.
  const [queuingProducts, setQueuingProducts] = useState<Set<string>>(new Set());
  // When a queued submission hits a large-change confirmation, the rest of
  // that product's drafts wait here until the dialog resolves.
  const [queueContinuation, setQueueContinuation] = useState<{ product: ProductRow; remaining: QueuedDraft[] } | null>(
    null,
  );
  const [toast, setToast] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null);
  const [skuReport, setSkuReport] = useState<SkuCopyResult | null>(null);
  // null means "everything", so a channel added later is included by default
  // rather than silently left out of every sync.
  const [syncOnly, setSyncOnly] = useState<Set<string> | null>(null);

  const canEdit = user.role === 'admin' || user.role === 'editor';

  const channelsQuery = useQuery({ queryKey: ['channels'], queryFn: api.channels });
  // Which fields each channel type accepts — needed to tell an editable SKU
  // mismatch from an Amazon one, which can never be copied (Amazon keys a
  // listing by its own SKU, so there is nothing to rename). Same query key
  // ChannelDetail already uses, so this is a cache hit rather than a second
  // request once any row has been expanded.
  const capabilitiesQuery = useQuery({ queryKey: ['capabilities'], queryFn: api.capabilities });
  // Every channel's configured business pricing rule — same query key
  // ChannelDetail uses, for the confirm dialog's "will also set the business
  // price to $X" line.
  const businessPricingQuery = useQuery({ queryKey: ['business-pricing'], queryFn: api.businessPricing });
  const productsQuery = useQuery({
    queryKey: ['products', search, filter, status, site],
    queryFn: () => api.products({ search, filter, status, site }),
  });

  /**
   * Every product with something outstanding — staged or pushed but not yet
   * settled — independent of the search/site/filter above, so a queued
   * change never hides just because someone happened to be searching for
   * something else. Sharing the ['products', ...] key prefix means every
   * existing invalidateQueries({queryKey: ['products']}) call already covers
   * this too.
   *
   * Disabled while `filter === 'sku_mismatch'`: that view's bulk-copy button
   * stages exactly what's on screen, and an in-flight row is not necessarily
   * SKU-mismatched — see the button below.
   *
   * runWriteRechecks settles rows on a background cron tick with no request
   * involved, so this needs its own polling rather than relying on
   * invalidation: fast while something is actively pushing, slower while
   * only waiting on the backoff schedule, off once nothing is outstanding.
   */
  const inFlightQuery = useQuery({
    queryKey: ['products', { view: 'in-flight', status }],
    queryFn: () => api.products({ filter: 'in_flight', status, limit: 500 }),
    enabled: filter !== 'sku_mismatch',
    refetchInterval: (query) => {
      const writes = (query.state.data?.products ?? []).flatMap((p) =>
        p.listings.flatMap((l) => l.unsettledWrites),
      );
      if (writes.some((w) => w.writeState === 'in_flight')) return 2000;
      if (writes.some((w) => w.writeState === 'awaiting_confirmation')) return 30_000;
      return false;
    },
    throwOnError: false,
  });

  const syncMutation = useMutation({
    mutationFn: (channelIds?: string[]) => api.sync(channelIds),
    onSuccess: (data) => {
      const failures = data.results.filter((r) => r.error);
      const seen = data.results.reduce((sum, r) => sum + r.listingsSeen, 0);
      const renamed = data.results.reduce((sum, r) => sum + (r.skuRenamed ?? 0), 0);
      const conflicts = data.results.flatMap((r) => r.skuRenameConflicts ?? []);

      const notes = [
        renamed > 0 ? `${renamed} SKU change${renamed === 1 ? '' : 's'} reconciled` : null,
        // A SKU collision needs a person, not another sync — surface it as its
        // own line rather than letting it hide inside an otherwise-ok toast.
        conflicts.length > 0
          ? `${conflicts.length} SKU change${conflicts.length === 1 ? '' : 's'} need attention: ${conflicts.join(', ')}`
          : null,
      ].filter((n): n is string => n !== null);

      setToast(
        failures.length
          ? {
              kind: 'warn',
              // The point of refreshing one channel is to see why it broke, so
              // the reason travels with the name rather than being left in a log.
              text: `Refreshed ${seen} listings; ${failures.length} failed — ${failures
                .map((f) => `${f.channelName}: ${f.error}`)
                .join('; ')}`,
            }
          : {
              kind: conflicts.length > 0 ? 'warn' : 'ok',
              text: [
                `Refreshed ${seen} listings across ${data.results.length} channels`,
                ...notes,
              ].join(' — '),
            },
      );
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['unlinked'] });
    },
    onError: (error: Error) => setToast({ kind: 'error', text: error.message }),
  });

  const channels = useMemo(
    () => (channelsQuery.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [channelsQuery.data],
  );

  // The marketplaces only. The two storefronts are where products come from, so
  // "is it listed there" is not a question worth a column.
  const marketplaces = useMemo(
    () => channels.filter((c) => c.type !== 'drupal_uc' && c.type !== 'drupal_commerce'),
    [channels],
  );

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleChannel = (key: string) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSection = (key: 'in-flight' | 'all') => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const beginEdit = (listing: Listing, cellKey: string) => {
    // A price-specific block (an Etsy listing pricing per variation) refuses
    // this exactly as a blanket one does — PriceCell's own gutter/tooltip
    // already reads both, this just has to agree with it.
    if (!canEdit || listing.writeBlockedReason || listing.priceWriteBlockedReason) return;
    setEditing(cellKey);
    setDraft(listing.priceMinor === null ? '' : (listing.priceMinor / 100).toFixed(2));
  };

  const invalidateAfterStage = () => {
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
  };

  const draftKey = (listingId: string, field: 'price' | 'title' | 'sku') => `${listingId}:${field}`;

  /** The local, unqueued draft for one listing's field, if any. */
  const draftFor = (listingId: string, field: 'price' | 'title' | 'sku'): DraftEdit | undefined =>
    draftEdits.get(draftKey(listingId, field));

  /**
   * Adds, replaces, or (if the value now matches what the listing already
   * shows) removes one field's entry in a draft map. Never mutates `map` —
   * always returns a fresh one, so it's safe to use both as a state updater
   * and to build a one-off snapshot to hand straight to queueProductChanges.
   */
  const upsertDraft = (
    map: Map<string, DraftEdit>,
    listing: Listing,
    field: 'price' | 'title' | 'sku',
    value: number | string,
    productTitle: string,
  ): Map<string, DraftEdit> => {
    const key = draftKey(listing.id, field);
    const next = new Map(map);
    // Null (no sku/price on record) and '' both mean "nothing set" — treated
    // as equal so flushing an untouched, already-empty field on Ctrl+Enter
    // never creates a phantom draft.
    if (String(value) === (currentFieldValue(listing, field) ?? '')) {
      next.delete(key);
    } else {
      next.set(key, { listing, field, value, productTitle });
    }
    return next;
  };

  /** Commits a field edit locally only — see DraftEdit. Nothing is sent yet. */
  const requestWrite = (listing: Listing, field: 'price' | 'title' | 'sku', value: number | string, productTitle: string) => {
    setDraftEdits((prev) => upsertDraft(prev, listing, field, value, productTitle));
  };

  /**
   * Ctrl/Cmd+Enter: commits this field's edit the same way requestWrite does,
   * then immediately submits every draft this product now has — including
   * ones from other fields already sitting in draftEdits, and even when this
   * particular field turned out unchanged.
   */
  const requestWriteAndQueue = (
    listing: Listing,
    field: 'price' | 'title' | 'sku',
    value: number | string,
    product: ProductRow,
  ) => {
    const next = upsertDraft(draftEdits, listing, field, value, product.title);
    setDraftEdits(next);
    queueProductChanges(product, next);
  };

  /**
   * Submits one product's queued drafts in order. Reuses the same
   * needs_confirmation → ConfirmWriteDialog escalation a direct edit used to
   * get from stageChange — a large price jump still pauses here for a second
   * look, it just may now be one of several drafts going out at once.
   */
  const processQueue = async (product: ProductRow, items: QueuedDraft[]) => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      const { key, entry } = item;
      try {
        await api.queueChange(entry.listing.id, entry.field, entry.value);
        setDraftEdits((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } catch (err) {
        const body = (err as any)?.body;
        if (body?.status === 'needs_confirmation') {
          setPending({
            listing: entry.listing,
            field: entry.field,
            newValue: entry.value,
            productTitle: entry.productTitle,
            percentChange: body.warning?.percentChange ?? 0,
          });
          setQueueContinuation({ product, remaining: items.slice(i + 1) });
          return; // paused — resumed by onPendingStaged, or left for a retry if cancelled
        }
        setToast({ kind: 'error', text: body?.message ?? (err instanceof Error ? err.message : String(err)) });
        setQueuingProducts((prev) => {
          const next = new Set(prev);
          next.delete(product.id);
          return next;
        });
        return; // stop; this and any remaining drafts stay staged locally for a retry
      }
    }
    invalidateAfterStage();
    setToast({ kind: 'ok', text: 'Staged — sync the queue to publish it.' });
    setQueuingProducts((prev) => {
      const next = new Set(prev);
      next.delete(product.id);
      return next;
    });
  };

  /** The "Queue Changes" button: submits every draft currently held for this product's listings. */
  const queueProductChanges = (product: ProductRow, draftsMap: Map<string, DraftEdit> = draftEdits) => {
    const items: QueuedDraft[] = product.listings.flatMap((l) =>
      (['price', 'title', 'sku'] as const)
        .map((field) => draftKey(l.id, field))
        .filter((key) => draftsMap.has(key))
        .map((key) => ({ key, entry: draftsMap.get(key)! })),
    );
    if (items.length === 0) return;
    setQueuingProducts((prev) => new Set(prev).add(product.id));
    void processQueue(product, items);
  };

  /** Discards every local draft for a product without contacting the server. */
  const discardProductChanges = (product: ProductRow) => {
    setDraftEdits((prev) => {
      const next = new Map(prev);
      for (const l of product.listings) {
        for (const field of ['price', 'title', 'sku'] as const) next.delete(draftKey(l.id, field));
      }
      return next;
    });
  };

  const commitPriceEdit = (listing: Listing, productTitle: string) => {
    setEditing(null);
    const parsed = parsePrice(draft);
    if (parsed === null) {
      setToast({ kind: 'error', text: `"${draft}" is not a valid price` });
      return;
    }
    requestWrite(listing, 'price', parsed, productTitle);
  };

  const commitPriceEditAndQueue = (listing: Listing, product: ProductRow) => {
    setEditing(null);
    const parsed = parsePrice(draft);
    if (parsed === null) {
      setToast({ kind: 'error', text: `"${draft}" is not a valid price` });
      return;
    }
    requestWriteAndQueue(listing, 'price', parsed, product);
  };

  const confirmPending = (): Promise<QueueOutcome> => {
    if (!pending) throw new Error('No pending change');
    return api.queueChange(pending.listing.id, pending.field, pending.newValue, true);
  };

  const onPendingStaged = (outcome: QueueOutcome) => {
    if (!pending) return;
    const key = draftKey(pending.listing.id, pending.field);
    const continuation = queueContinuation;
    setPending(null);
    setQueueContinuation(null);
    if (outcome.ok) {
      setDraftEdits((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
    setToast(
      outcome.ok
        ? { kind: 'ok', text: 'Staged — sync the queue to publish it.' }
        : { kind: 'error', text: outcome.message },
    );
    invalidateAfterStage();
    if (!continuation) return;
    if (outcome.ok && continuation.remaining.length > 0) {
      void processQueue(continuation.product, continuation.remaining);
    } else {
      setQueuingProducts((prev) => {
        const next = new Set(prev);
        next.delete(continuation.product.id);
        return next;
      });
    }
  };

  /** The dialog was dismissed without confirming — the rest of this product's drafts stay put for a later retry. */
  const cancelPending = () => {
    if (queueContinuation) {
      setQueuingProducts((prev) => {
        const next = new Set(prev);
        next.delete(queueContinuation.product.id);
        return next;
      });
    }
    setPending(null);
    setQueueContinuation(null);
  };

  /**
   * Turns a SKU-copy result into feedback. A clean run (everything either
   * queued or already matching) gets the same one-line toast every other
   * staged edit does; anything genuinely skipped — blocked, a collision, a
   * duplicate target — earns the fuller report, since "12 queued" alone would
   * hide why 4 more listings were left untouched.
   */
  const reportSkuCopy = (result: SkuCopyResult) => {
    const outcomes = result.products.flatMap((p) => p.outcomes);
    const problems = outcomes.filter((o) => o.status === 'skipped' && o.reason !== 'already_matches');
    if (problems.length === 0) {
      setToast({
        kind: 'ok',
        text:
          result.queued > 0
            ? `Staged ${result.queued} SKU change${result.queued === 1 ? '' : 's'} — sync the queue to publish ${result.queued === 1 ? 'it' : 'them'}.`
            : 'Every channel already matches the website SKU.',
      });
    } else {
      setSkuReport(result);
    }
    invalidateAfterStage();
  };

  const copySkuMutation = useMutation({
    mutationFn: (productId: string) => api.copySkuForProduct(productId),
    onSuccess: reportSkuCopy,
    onError: (error: Error) => setToast({ kind: 'error', text: error.message }),
  });

  /** Acknowledges a write parked at "not confirmed" or "reverted by channel" — never touches the channel. */
  const dismissMutation = useMutation({
    mutationFn: (editLogId: string) => api.dismissWrite(editLogId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
    onError: (error: Error) => setToast({ kind: 'error', text: error.message }),
  });

  /**
   * Clears every staged change and every unsettled write for one product row
   * — including one still genuinely in flight or awaiting confirmation,
   * which the dismiss action above deliberately leaves alone.
   */
  const clearProductChangesMutation = useMutation({
    mutationFn: (productId: string) => api.clearProductChanges(productId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
      const total = result.clearedPending + result.clearedWrites;
      setToast({ kind: 'ok', text: `Cleared ${total} change${total === 1 ? '' : 's'} for this product.` });
    },
    onError: (error: Error) => setToast({ kind: 'error', text: error.message }),
  });

  /**
   * Plans, confirms, then stages — in that order. The button only knows how
   * many *products* it's about to sweep; the number that actually matters is
   * how many *listings* would change, which only a real plan (a dry run) can
   * answer. Threshold and breakdown follow PendingBar's own "Sync all", which
   * confirms past a handful of rows or anything sheet-sourced — here, past a
   * handful of listings, since this stages live marketplace rewrites.
   */
  const copySkuBulkMutation = useMutation({
    mutationFn: async (productIds: string[]): Promise<SkuCopyResult | null> => {
      const preview = await api.copySkuForProducts(productIds, true);
      if (preview.queued === 0) return preview; // nothing to stage — the report explains why

      const outcomes = preview.products.flatMap((p) => p.outcomes);
      const alreadyMatching = outcomes.filter((o) => o.reason === 'already_matches').length;
      const unwritable = outcomes.filter((o) => o.status === 'skipped' && o.reason !== 'already_matches').length;
      const productsAffected = preview.products.filter((p) => p.outcomes.some((o) => o.status === 'queued')).length;

      if (
        preview.queued > 5 &&
        !window.confirm(
          `Stage ${preview.queued} SKU change${preview.queued === 1 ? '' : 's'} across ${productsAffected} product${productsAffected === 1 ? '' : 's'}?\n\n` +
            `${alreadyMatching} listing${alreadyMatching === 1 ? '' : 's'} already match and ${unwritable} cannot be changed.\n\n` +
            `Nothing is pushed to any marketplace until you sync the queue.`,
        )
      ) {
        return null; // cancelled — the plan was only ever a preview
      }

      return api.copySkuForProducts(productIds, false);
    },
    onSuccess: (result) => {
      if (result) reportSkuCopy(result);
    },
    onError: (error: Error) => setToast({ kind: 'error', text: error.message }),
  });

  if (channelsQuery.isLoading || productsQuery.isLoading || inFlightQuery.isLoading) {
    return <div className="empty-state">Loading…</div>;
  }
  if (productsQuery.error) {
    return <div className="alert error">Could not load products: {(productsQuery.error as Error).message}</div>;
  }

  const products = productsQuery.data?.products ?? [];
  // Pinned above the ordinary list, and excluded from it below so nothing
  // renders twice. Empty (rather than just unused) while the sku_mismatch
  // filter is up — see inFlightQuery's `enabled` above.
  const inFlightProducts = inFlightQuery.data?.products ?? [];
  const inFlightIds = new Set(inFlightProducts.map((p) => p.id));
  const mainProducts = products.filter((p) => !inFlightIds.has(p.id));
  const fullSpan = tableColumnSpan(channels, marketplaces);

  // Union of both groups — otherwise "Collapse all" would read wrong while
  // the pinned rows sit collapsed, and clicking it would only touch the
  // ordinary rows.
  const allVisibleProducts = [...inFlightProducts, ...mainProducts];
  const allExpanded = allVisibleProducts.length > 0 && allVisibleProducts.every((p) => expanded.has(p.id));

  const renderSectionHeader = (key: 'in-flight' | 'all', label: string) => {
    const collapsed = collapsedSections.has(key);
    return (
      <tr className={`section-header section-header-${key}`}>
        <td colSpan={fullSpan}>
          <button
            type="button"
            className="section-toggle-btn"
            onClick={() => toggleSection(key)}
            aria-expanded={!collapsed}
          >
            <span className={`section-toggle${collapsed ? ' collapsed' : ''}`} aria-hidden="true">
              ▾
            </span>
            {label}
          </button>
        </td>
      </tr>
    );
  };

  const renderProductRow = (product: ProductRow, inFlight: boolean) => {
    // Every field of this product's listings that has a local, unqueued
    // edit sitting in draftEdits — computed fresh each render rather than
    // stored per-product, since the map itself is the only source of truth.
    const productDraftKeys = product.listings.flatMap((l) =>
      (['price', 'title', 'sku'] as const).map((field) => draftKey(l.id, field)).filter((key) => draftEdits.has(key)),
    );
    const isQueuing = queuingProducts.has(product.id);

    return (
    <Fragment key={inFlight ? `inflight:${product.id}` : product.id}>
      {/* Tinted by storefront so the two catalogues are separable
          at a glance; a product on both gets the striped class. */}
      <tr className={`src-${product.sites.join('-') || 'none'}${inFlight ? ' row-in-flight' : ''}`}>
        <td>
          {product.sites.map((s) => (
            <span key={s} className={`pill pill-${s}`}>
              {s.toUpperCase()}
            </span>
          ))}
        </td>
        <td>
          <button
            className="expand-btn"
            onClick={() => toggleExpanded(product.id)}
            aria-label={expanded.has(product.id) ? 'Collapse' : 'Expand'}
          >
            {expanded.has(product.id) ? '▾' : '▸'}
          </button>
        </td>
        <td>
          {product.imageUrl ? (
            <img className="thumb" src={product.imageUrl} alt="" loading="lazy" />
          ) : (
            <div className="thumb" />
          )}
        </td>
        <td className="title-cell">
          {product.title}
          {product.hasWriteError && <> <span className="badge error">write failed</span></>}
          {/* Only in the pinned group — clears every staged change and
              unsettled write for this product in one action, including one
              still genuinely in flight. Confirmed first since it can abandon
              a write nothing has actually resolved yet. */}
          {inFlight && canEdit && (
            <button
              className="link small danger"
              title="Clear all changes for this product"
              aria-label="Clear all changes for this product"
              disabled={clearProductChangesMutation.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Clear every staged change and unsettled write for "${product.title}"? This wipes local ` +
                      `tracking only — it never touches the channel — and cannot be undone.`,
                  )
                ) {
                  clearProductChangesMutation.mutate(product.id);
                }
              }}
            >
              ✕
            </button>
          )}
          {/* Only in the pinned group — one chip per queued change and per
              unsettled write, so what's outstanding is legible without
              opening the row. */}
          {inFlight && (
            <ProductChangeSummary
              product={product}
              onDismiss={(editLogId) => dismissMutation.mutate(editLogId)}
              dismissing={dismissMutation.isPending ? dismissMutation.variables : null}
            />
          )}
          {/* Appears the moment any field on this product has an edited-but-
              not-yet-queued value — on either group, since a product already
              in flight for one field can still pick up more local edits
              before its next queue push. Nothing here has reached the server
              yet, so the row stays put until this is clicked (or Ctrl+Enter
              is pressed while editing a field). */}
          {canEdit && productDraftKeys.length > 0 && (
            <span className="queue-changes-controls">
              <button
                type="button"
                className="small primary queue-changes-btn"
                disabled={isQueuing}
                onClick={() => queueProductChanges(product)}
              >
                {isQueuing ? 'Queuing…' : `Queue Changes (${productDraftKeys.length})`}
              </button>
              <button
                type="button"
                className="link small discard-changes-btn"
                disabled={isQueuing}
                title="Discard these unqueued edits — never touches the server"
                onClick={() => discardProductChanges(product)}
              >
                Discard
              </button>
            </span>
          )}
        </td>
        <td className="sku">
          {product.sku}
          {/* Sheet matching is by SKU, so the fact that the sheet
              has no row for this one belongs next to it. */}
          {product.sheetLink === 'missing' && (
            <SheetLinkMark seenAt={product.sheetSeenAt} />
          )}
          {/* Sits with the SKU for the same reason the sheet mark does —
              this is a fact about the SKU. Hidden when every mismatch on
              the row is Amazon's, which can never be copied (see
              hasEditableSkuMismatch) — there is nothing this button can
              fix there, so it no longer offers to. */}
          {canEdit && hasEditableSkuMismatch(product, capabilitiesQuery.data) && (
            <button
              className="link small"
              disabled={copySkuMutation.isPending}
              title={`Copy ${product.sku} to ${product.listings.filter((l) => l.skuMismatch).length} channel${
                product.listings.filter((l) => l.skuMismatch).length === 1 ? '' : 's'
              } that don't match`}
              onClick={() => copySkuMutation.mutate(product.id)}
            >
              copy →
            </button>
          )}
        </td>

        {marketplaces.map((channel) => {
          const listed = product.listings.some((l) => l.channelId === channel.id);
          return (
            <td key={`listed-${channel.id}`} className="listed-col">
              <ListedMark listed={listed} channelName={channel.name} />
            </td>
          );
        })}

        {channels.map((channel) => (
          <PriceCell
            key={channel.id}
            channel={channel}
            product={product}
            referenceName={
              channels.find((c) => c.id === product.primarySiteChannelId)?.name ??
              'website'
            }
            canEdit={canEdit}
            cellKey={`${product.id}:${channel.id}`}
            editingKey={editing}
            draft={draft}
            setDraft={setDraft}
            onBeginEdit={beginEdit}
            onCommit={commitPriceEdit}
            onCommitAndQueue={commitPriceEditAndQueue}
            onCancel={() => setEditing(null)}
            draftFor={draftFor}
          />
        ))}
      </tr>

      {/* Rendered straight into the table body, not wrapped in a
          full-width cell: the channel rows are real rows, and that
          is what puts each channel's price under its own heading. */}
      {expanded.has(product.id) && (
        <ChannelDetail
          product={product}
          channels={channels}
          marketplaces={marketplaces}
          canEdit={canEdit}
          expandedChannels={expandedChannels}
          onToggleChannel={toggleChannel}
          onEdited={() => {
            void queryClient.invalidateQueries({ queryKey: ['products'] });
            void queryClient.invalidateQueries({ queryKey: ['unlinked'] });
            void queryClient.invalidateQueries({ queryKey: ['queue'] });
          }}
          onToast={setToast}
          onRequestWrite={(listing, field, newValue) =>
            requestWrite(listing, field, newValue, product.title)
          }
          onRequestWriteAndQueue={(listing, field, newValue) =>
            requestWriteAndQueue(listing, field, newValue, product)
          }
          draftFor={draftFor}
          priceEdit={{
            referenceName:
              channels.find((c) => c.id === product.primarySiteChannelId)?.name ??
              'website',
            editingKey: editing,
            draft,
            setDraft,
            onBeginEdit: beginEdit,
            onCommit: commitPriceEdit,
            onCommitAndQueue: commitPriceEditAndQueue,
            onCancel: () => setEditing(null),
          }}
        />
      )}
    </Fragment>
    );
  };

  return (
    <div>
      <div className="toolbar">
        <select
          value={site}
          onChange={(e) => setSite(e.target.value as 'all' | SiteCode)}
          aria-label="Storefront"
        >
          <option value="all">All furniture</option>
          <option value="mlf">Log Furniture</option>
          <option value="mff">Farmhouse Furniture</option>
        </select>
        <input
          type="search"
          placeholder="Search SKU or title… (press Enter)"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            // The type="search" field's own clear (x) button fires this with an
            // empty value and no keydown — clear the filter immediately rather
            // than leaving results stuck on the last submitted search term.
            if (e.target.value === '') setSearch('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch(searchInput);
          }}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value as ProductFilter)}>
          <option value="all">All products</option>
          <option value="mismatch">Marketplace price mismatches only</option>
          <option value="errors">Write errors only</option>
          <option value="no_sheet">Missing from the Google Sheet</option>
          <option value="sku_mismatch">Marketplace SKU mismatches only</option>
        </select>
        <span className="muted small">
          {mainProducts.length} of {productsQuery.data?.total ?? 0} products
        </span>
        <div className="spacer" />
        {/* Only offered once the filter is already narrowed to mismatches —
            an always-visible sweep button invites a click on the unfiltered
            catalogue, and choosing to look at mismatches first is what makes
            "all shown" a reach the user actually intended. Uses mainProducts,
            not products: the in-flight group is suppressed for this filter
            (see inFlightQuery's `enabled` above), so the two are always the
            same set here — this just says so explicitly rather than by
            coincidence. */}
        {canEdit && filter === 'sku_mismatch' && mainProducts.length > 0 && (
          <button
            className="link small"
            disabled={copySkuBulkMutation.isPending}
            onClick={() => copySkuBulkMutation.mutate(mainProducts.map((p) => p.id))}
          >
            {copySkuBulkMutation.isPending ? 'Staging…' : `Copy website SKU for all ${mainProducts.length} shown`}
          </button>
        )}
        {/* Product rows only, across both groups. The channel rows inside
            them stay as they were, which keeps "expand everything" from
            mounting every channel's history and extra fields at once. */}
        <button
          className="link small"
          disabled={allVisibleProducts.length === 0}
          onClick={() =>
            setExpanded(allExpanded ? new Set() : new Set(allVisibleProducts.map((p) => p.id)))
          }
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {canEdit && (
        <SyncCard
          channels={channels}
          selected={syncOnly}
          onChange={setSyncOnly}
          busy={syncMutation.isPending}
          onSync={() => syncMutation.mutate(syncOnly ? [...syncOnly] : undefined)}
        />
      )}

      {toast && (
        <div className={`alert ${toast.kind}`} onClick={() => setToast(null)} role="status">
          {toast.text}
        </div>
      )}

      {skuReport && <SkuCopyReport result={skuReport} onDismiss={() => setSkuReport(null)} />}

      {allVisibleProducts.length === 0 ? (
        <div className="empty-state">
          <p>No products yet.</p>
          <p className="small">
            Products are seeded from the two websites. Configure their bridge credentials in Admin,
            then run a sync.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 52 }}>Source</th>
                <th style={{ width: 30 }} />
                <th style={{ width: 44 }} />
                <th>Product</th>
                <th>SKU</th>
                {/* Which marketplaces this product actually reaches, at a
                    glance — the price columns answer "how much", not "where". */}
                {marketplaces.map((channel) => (
                  <th key={`listed-${channel.id}`} className="listed-col" title={channel.name}>
                    {shortLabel(channel.name)}
                  </th>
                ))}
                {channels.map((channel) => {
                  // Marketplace columns carry the percentage strip, so their
                  // heading needs the extra inset to stay over the figures
                  // rather than over figure-plus-strip.
                  const isSite = channel.type === 'drupal_uc' || channel.type === 'drupal_commerce';
                  return (
                    <th
                      key={channel.id}
                      className={isSite ? 'price-col' : 'price-col market-col'}
                      title={
                        isSite
                          ? undefined
                          : 'In brackets: how far above the website price this channel sits'
                      }
                    >
                      {channel.name}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {inFlightProducts.length > 0 &&
                renderSectionHeader(
                  'in-flight',
                  inFlightQuery.data && inFlightQuery.data.total > inFlightProducts.length
                    ? `Showing ${inFlightProducts.length} of ${inFlightQuery.data.total} products with changes in flight`
                    : `${inFlightProducts.length} product${inFlightProducts.length === 1 ? '' : 's'} with changes in flight`,
                )}
              {!collapsedSections.has('in-flight') &&
                inFlightProducts.map((product) => renderProductRow(product, true))}
              {inFlightProducts.length > 0 && renderSectionHeader('all', 'All products')}
              {!collapsedSections.has('all') && mainProducts.map((product) => renderProductRow(product, false))}
            </tbody>
          </table>
        </div>
      )}

      {pending && (
        <ConfirmWriteDialog
          channelName={pending.listing.channelName}
          productTitle={pending.productTitle}
          field={pending.field}
          oldValue={
            pending.field === 'price'
              ? formatPrice(pending.listing.priceMinor, pending.listing.currency)
              : pending.listing.title
          }
          newValue={
            pending.field === 'price'
              ? formatPrice(Number(pending.newValue), pending.listing.currency)
              : String(pending.newValue)
          }
          percentChange={pending.percentChange}
          businessPrice={pendingBusinessPricePreview(
            pending,
            capabilitiesQuery.data,
            businessPricingQuery.data?.rules,
          )}
          onCancel={cancelPending}
          onConfirm={confirmPending}
          onStaged={onPendingStaged}
        />
      )}
    </div>
  );
}

/**
 * What the confirm dialog should say about the business price, or null when
 * there is nothing to say — every field but price, every channel but Amazon,
 * and any listing with no business price on record. Mirrors ChannelDetail's
 * own effectiveBusinessRule precedence (listing override, else channel rule,
 * else mirror).
 */
function pendingBusinessPricePreview(
  pending: PendingWrite,
  capabilities: Record<string, ChannelCapabilities> | undefined,
  rules: ChannelBusinessPricingRule[] | undefined,
): { from: string; to: string; label: string } | null {
  if (pending.field !== 'price') return null;
  if (!capabilities?.[pending.listing.channelType]?.canWriteBusinessPrice) return null;
  if (pending.listing.businessPriceMinor === null) return null;

  const channelRule = rules?.find((r) => r.channelId === pending.listing.channelId)?.rule ?? MIRROR_RETAIL;
  const rule = pending.listing.businessPriceRule ?? channelRule;
  const toMinor = deriveBusinessPrice(Number(pending.newValue), rule);

  return {
    from: formatPrice(pending.listing.businessPriceMinor, pending.listing.currency),
    to: formatPrice(toMinor, pending.listing.currency),
    label: businessPriceRuleLabel(rule),
  };
}

/** What a listing currently shows for a field — the reference a queued chip's "old" side compares against. */
function currentFieldValue(listing: Listing, field: 'price' | 'title' | 'sku'): string | null {
  if (field === 'price') return listing.priceMinor === null ? null : String(listing.priceMinor);
  if (field === 'title') return listing.title;
  return listing.sku;
}

/**
 * Whether a product has at least one SKU mismatch the copy action could
 * actually fix — mirrors ChannelDetail's own `canEditSku` (channel-level
 * capability, then the per-listing blocks) rather than inventing a second
 * way to ask the same question.
 *
 * `hasSkuMismatch`/`skuMismatch` are deliberately capability-blind (see
 * routes/products.ts): a mismatch is still a fact even on a channel that can
 * never accept the write, e.g. Amazon, which keys a listing by its own SKU.
 * That's right for the mismatch *indicator*, which should stay lit either
 * way — but the copy button is an offer to fix it, and there is nothing to
 * offer when every mismatch on the row is Amazon's.
 */
function hasEditableSkuMismatch(
  product: ProductRow,
  capabilities: Record<string, ChannelCapabilities> | undefined,
): boolean {
  return product.listings.some(
    (l) =>
      l.skuMismatch &&
      capabilities?.[l.channelType]?.canWriteSku === true &&
      !l.writeBlockedReason &&
      !l.skuWriteBlockedReason,
  );
}

/**
 * One chip per queued change and per unsettled write, shown under a pinned
 * row's title. Reuses ChannelTag/Change from ChangeBits.tsx — the same
 * formatting the Pending tab and History already use — so a price reads as
 * money here too.
 */
function ProductChangeSummary({
  product,
  onDismiss,
  dismissing,
}: {
  product: ProductRow;
  onDismiss: (editLogId: string) => void;
  /** The editLogId currently being dismissed, if any — disables its own button mid-request. */
  dismissing: string | null;
}) {
  const chips: JSX.Element[] = [];

  for (const listing of product.listings) {
    for (const change of listing.pendingChanges) {
      chips.push(
        <div key={`pc:${change.id}`} className="in-flight-chip">
          <ChannelTag name={listing.channelName} type={listing.channelType} />
          <Change
            field={change.field}
            oldValue={currentFieldValue(listing, change.field)}
            newValue={change.newValue}
            currency={listing.currency}
          />
          <QueueStatusBadge status={change.status} />
        </div>,
      );
    }
    for (const write of listing.unsettledWrites) {
      chips.push(
        <div key={`uw:${listing.id}:${write.field}`} className="in-flight-chip" title={write.message ?? undefined}>
          <ChannelTag name={listing.channelName} type={listing.channelType} />
          <Change field={write.field} oldValue={write.oldValue} newValue={write.newValue} currency={listing.currency} />
          <WriteStateBadge
            state={write.writeState}
            attempts={write.checkAttempts}
            onDismiss={() => onDismiss(write.id)}
            dismissing={dismissing === write.id}
          />
        </div>,
      );
    }
  }

  if (chips.length === 0) return null;
  return <div className="in-flight-chips">{chips}</div>;
}

/** A queued change's own status — mirrors PendingChanges.tsx's StatusBadge, for the embedded (not full) queue entry shape. */
function QueueStatusBadge({
  status,
}: {
  status: 'queued' | 'executing' | 'conflict' | 'needs_confirmation' | 'failed';
}) {
  if (status === 'executing') {
    return <span className="badge" title="Being pushed right now">pushing…</span>;
  }
  if (status === 'conflict') {
    return (
      <span className="badge warn" title="The channel's price has changed since this was staged — see the Pending tab">
        conflict
      </span>
    );
  }
  if (status === 'needs_confirmation') {
    return (
      <span className="badge warn" title="This price change is larger than usual and needs a second look — see the Pending tab">
        needs confirmation
      </span>
    );
  }
  if (status === 'failed') {
    return <span className="badge error">failed</span>;
  }
  return <span className="badge">queued</span>;
}

/** What stage a pushed-but-unsettled write is at — see writeStateOf (api's lib/writes.ts) for the derivation. */
function WriteStateBadge({
  state,
  attempts,
  onDismiss,
  dismissing,
}: {
  state: 'in_flight' | 'awaiting_confirmation' | 'reverted_by_channel' | 'not_confirmed';
  attempts: number;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  if (state === 'in_flight') {
    return <span className="badge" title="Pushed just now — waiting on the channel to accept it">pushing…</span>;
  }
  if (state === 'awaiting_confirmation') {
    return (
      <span className="badge warn" title={`Accepted, but not yet confirmed live — recheck ${attempts + 1} of 4`}>
        awaiting confirmation
      </span>
    );
  }
  // reverted_by_channel and not_confirmed are the two terminal states nothing
  // here ever resolves automatically — see writeStateOf's own comment on
  // dismissedAt — so both offer a way to acknowledge and stop watching them.
  const label = state === 'reverted_by_channel' ? 'reverted by channel' : 'not confirmed';
  const title =
    state === 'reverted_by_channel'
      ? 'Confirmed live, then the channel reverted it'
      : 'Pushed, but never confirmed live';
  return (
    <>
      <span className="badge error" title={title}>
        {label}
      </span>
      <button
        type="button"
        className="link small"
        disabled={dismissing}
        title="Acknowledge — stops this from counting as outstanding. Never touches the channel."
        onClick={onDismiss}
      >
        {dismissing ? 'dismissing…' : 'dismiss'}
      </button>
    </>
  );
}

/**
 * Whether the product reaches a marketplace.
 *
 * Drawn rather than rendered as a checkbox input: nothing in this column is
 * toggleable, and a real checkbox invites a click that would do nothing. The
 * mark carries its own label so it is not colour alone doing the work.
 */
function ListedMark({ listed, channelName }: { listed: boolean; channelName: string }) {
  const label = `${listed ? 'Listed' : 'Not listed'} on ${channelName}`;
  return (
    <svg
      className={`listed-mark${listed ? ' is-listed' : ''}`}
      viewBox="0 0 16 16"
      width="15"
      height="15"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {listed ? (
        <path
          d="M3.5 8.5l3 3 6-6.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  );
}

/**
 * This SKU has no row in the Google Sheet.
 *
 * Only ever rendered when the server has a full sheet pull to measure against
 * — before that the answer is unknown, and an indicator on every row would say
 * something the app cannot back up. A broken chain rather than a warning
 * triangle: the sheet not carrying a SKU is a gap to close, not a fault.
 */
function SheetLinkMark({ seenAt }: { seenAt: string | null }) {
  const label = seenAt
    ? `Last seen in the Google Sheet on ${new Date(seenAt).toLocaleDateString()}; it has no row there now`
    : 'No row in the Google Sheet for this SKU';
  return (
    <svg
      className="sheet-link-mark"
      viewBox="0 0 16 16"
      width="13"
      height="13"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {/* Two link halves pulling apart, with the break in the middle. */}
      <path
        d="M6.5 4.5L5 3a2.5 2.5 0 00-3.5 3.5L3 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M9.5 11.5L11 13a2.5 2.5 0 003.5-3.5L13 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <line x1="6.5" y1="9.5" x2="9.5" y2="6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * A column heading narrow enough to sit above a mark.
 *
 * "Etsy — Log Furniture" becomes "Etsy LF"; the full name stays in the title
 * attribute, so nothing is lost for anyone who needs it.
 */
function shortLabel(name: string): string {
  const [head, tail] = name.split('—').map((part) => part.trim());
  if (!tail) return head ?? name;
  const initials = tail
    .replace(/[()]/g, '')
    .split(/\s+/)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
  return `${head} ${initials}`;
}

/**
 * Manual refresh, with the channels to include.
 *
 * This reads listings in from the channels — it never pushes anything.
 * "Sync" is reserved for PendingBar's push button, so this doesn't share the
 * word with the one control that writes to a channel.
 *
 * Refreshing all six is the normal case, but when one channel is failing the
 * point is to retry that one alone and read its error, rather than waiting on
 * the five that are fine. Everything is checked by default, and an empty
 * selection disables the button instead of quietly refreshing nothing.
 */
function SyncCard({
  channels,
  selected,
  onChange,
  busy,
  onSync,
}: {
  channels: Channel[];
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
  busy: boolean;
  onSync: () => void;
}) {
  const isOn = (id: string) => selected === null || selected.has(id);
  const onCount = channels.filter((c) => isOn(c.id)).length;

  const toggle = (id: string) => {
    const next = new Set(selected ?? channels.map((c) => c.id));
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Back to null when everything is on, so later channels stay included.
    onChange(next.size === channels.length ? null : next);
  };

  return (
    <div className="card sync-card">
      <div className="sync-channels">
        {channels.map((channel) => (
          <label key={channel.id} className="sync-channel">
            <input type="checkbox" checked={isOn(channel.id)} onChange={() => toggle(channel.id)} />
            {channel.name}
          </label>
        ))}
      </div>

      <div className="sync-actions">
        <button className="link small" onClick={() => onChange(null)} disabled={selected === null}>
          all
        </button>
        <button className="link small" onClick={() => onChange(new Set())} disabled={onCount === 0}>
          none
        </button>
        <button className="primary" onClick={onSync} disabled={busy || onCount === 0}>
          {busy
            ? 'Refreshing…'
            : onCount === channels.length
              ? 'Refresh from channels'
              : `Refresh ${onCount} channel${onCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
